import { Dialog, DialogPanel } from "@headlessui/react";
import { type Signal, useSignal } from "@preact/signals";
import { Button, PrimaryButton } from "#palette/components/button";
import { RadioOptionGroup } from "#palette/components/form/radio";

// Internals ///////////////////////////////////////////////////////////////////
const reasonOptions = [
  `I want to create a PDF and export this Incident Log for the purpose of record preservation.
  I understand this document will be discoverable which means I may have to provide a copy of it in a court proceeding if ordered by a subpoena or court order.`,
  `I want to create a PDF and export this Incident Log for the purpose of sharing it with an attorney to seek and receive legal advice.
  I want this Incident Log to be confidential and protected by attorney client privilege and so I will not share it with anyone except my attorney.`,
];

// Disclaimer //////////////////////////////////////////////////////////////////
export function IncidentLogDisclaimer(props: {
  open: Signal<boolean>;
  onComplete: (legal: boolean) => void;
  onCancel: () => void;
}) {
  const selected = useSignal("");
  return (
    <Dialog
      open={props.open.value}
      onClose={() => {
        props.open.value = false;
        selected.value = "";
        props.onCancel();
      }}
      className="fixed inset-0 w-screen items-center justify-center bg-brand-900/20 p-4 transition duration-300 ease-out data-closed:opacity-0 z-50 flex"
    >
      <div className="w-xs lg:w-xl max-h-dvh overflow-y-auto">
        <DialogPanel className="mt-5 text-left rounded-lg p-5 flex flex-col gap-2 border border-brand-500 shadow content-start place-self-center bg-brand-0 w-xs lg:w-xl">
          <p>
            While we know that survivors often remember things in stages and
            this is typical, we also know how this can be used against
            survivors. As such, before downloading the Incident Log that
            includes information about what happened to you, you should be aware
            that differences between what you download and what you tell other
            people may cause various legal actors (including police or jury
            members) to question the accuracy of your recollections.
          </p>
          <p class="font-bold">
            For your own protection, we recommend that you password protect the
            PDF file after it is downloaded and store it in a place that is safe
            and that only you have access to.
          </p>
          <p>
            Callisto does not assume any responsibility for information that you
            record to or download from Callisto Vault, and no one shall be
            entitled to a claim for detrimental reliance on any information
            provided or expressed. Callisto does not endorse, guarantee, or
            warranty the accuracy, reliability, or thoroughness of any
            referenced information.
          </p>
          <p class="font-bold">Please select one of the following options:</p>
          <RadioOptionGroup options={reasonOptions} selected={selected} />
          <div className="flex justify-between mt-5">
            <Button
              onClick={() => {
                props.open.value = false;
                props.onCancel();
              }}
              className="max-w-fit"
            >
              Cancel
            </Button>
            <PrimaryButton
              onClick={() => {
                props.open.value = false;
                props.onComplete(selected.value === reasonOptions[1]);
              }}
              className="max-w-fit"
              disabled={selected.value === ""}
            >
              Download PDF
            </PrimaryButton>
          </div>
        </DialogPanel>
      </div>
    </Dialog>
  );
}
